import { MutationCache, QueryClient } from '@tanstack/react-query';
import { toast } from '../store/useToasts';

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'object' && error && 'body' in error) {
    const body = (error as { body?: { message?: string; reason?: string } }).body;
    if (body?.message) return body.message;
    if (body?.reason) return body.reason;
  }
  return 'The request did not complete. Please try again.';
}

/**
 * Shared TanStack Query client.
 *
 * Defaults tuned for the Forge console:
 *   - 30s stale time keeps active range/health views fresh without thrash.
 *   - 5m cache time retains data across route changes (instant back-nav).
 *   - Refetch on window focus is off — the console is meant to be left open;
 *     real-time updates land via WebSocket / EventBridge subscriptions, not poll.
 *   - Single retry on transient network errors; mutations never retry by default
 *     (idempotency is enforced server-side via Lambda Powertools).
 */
export const queryClient = new QueryClient({
  /**
   * Global mutation-failure surface. Before this, EVERY mutation (destroy,
   * hibernate, resume, start-phase, stop-lab, extend-lab) failed silently — the
   * spinner stopped and nothing else happened, so the operator would just click
   * again. A per-mutation `meta.errorTitle` refines the headline; opting out is
   * `meta: { silentError: true }` for flows that render their own inline error.
   */
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      const meta = mutation.options.meta as
        | { errorTitle?: string; silentError?: boolean }
        | undefined;
      if (meta?.silentError) return;
      toast.error(meta?.errorTitle ?? 'Action failed', errorMessage(error));
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
    mutations: {
      retry: 0,
    },
  },
});
